<template>
  <div class="one-thid-banner-box" :style="{ '--one-third-duration': duration + 's' }">
    <div v-if="confettiEnabled" id="one-third-animation-layer" ref="confettiContainer"></div>
    <div
      id="one-third-progress-bar"
      :class="'at-' + direction"
    ></div>
    <div
      id="one-third-banner-media"
      ref="mediaElement"
      :class="['at-' + direction, imageShown ? 'appear' : 'disappear']"
    >
      <img
        v-if="!isVideo"
        :src="resolvedContentUrl"
        rel="preload"
        class="media"
        @error="onMediaError"
      />
      <video
        v-else
        ref="videoElement"
        class="media"
        :src="hasSrcset ? null : resolvedContentUrl"
        preload="metadata"
        :autoplay="true"
        :loop="true"
        @error="onMediaError"
      >
        <template v-if="hasSrcset">
          <source
            v-for="(url, type) in resolvedContentSrcset"
            :key="type"
            :src="url"
            :type="type"
          />
        </template>
      </video>
      <qr-container
        v-if="qrPosition"
        :link="qrLink"
        :size="qrSize"
        class="banner-qr"
        :class="qrPosition"
      />
    </div>
  </div>
</template>

<script>
import { reportAdEvent } from '../services/adReport'
import { reportAdFailure } from '../services/adFailure'
import mediaFallback from '../mixins/mediaFallback'
import { initConfetti } from '../confetti'
import QrContainer from '../components/QrContainer'

/** Fallback cleanup if rendering is suspended before particles finish. */
const CONFETTI_DESTROY_AFTER_MS = 8000

export default {
  name: 'OneThirdBannerBox',
  mixins: [mediaFallback],
  components: {
    QrContainer
  },
  data () {
    return {
      imageShown: false,
      bannerSize: null,
      bannerTimeouts: null,
      confettiInstance: null
    }
  },
  props: {
    confettiEnabled: {
      type: Boolean,
      default: true
    },
    contentUrl: {
      type: String,
      required: true
    },
    contentSrcset: {
      type: Object,
      default: null
    },
    endUrl: {
      type: String,
      required: true
    },
    startUrl: {
      type: String,
      default: ''
    },
    direction: {
      type: String,
      required: true
    },
    duration: {
      type: Number,
      required: true
    },
    qrPosition: {
      type: String,
      default: null
    },
    qrLink: {
      type: String,
      default: null
    },
    isVideo: {
      type: Boolean,
      default: false
    },
    // Signed route on the monolith, the same one VideoBox reports to.
    failUrl: {
      type: String,
      default: ''
    },
    taskId: {
      type: [Number, String],
      default: null
    }
  },
  mounted () {
    try {
      this.imageShown = true
      this.bannerTimeouts = []
      // How long the creative hung before failing, for reportAdFailure.
      this._mountedAt = Date.now()

      // Reported here rather than alongside the finish, because this component
      // draws every one-third-banner and every non-fullscreen
      // extension-universal — the formats behind all 94 launches that reported
      // nothing over 22-26.08 — and a launch that reports a start and no finish
      // is a different fault from one that reports neither.
      reportAdEvent(this.startUrl, 'startUrl')

      const pushT = (id) => {
        this.bannerTimeouts.push(id)
      }

      pushT(setTimeout(() => {
        this.bannerSize = (this.direction === 'top' || this.direction === 'bottom') ? this.$refs.mediaElement.offsetHeight : this.$refs.mediaElement.offsetWidth
        if (!this.confettiEnabled) return
        const confettiData = initConfetti(this.$refs.confettiContainer)
        this.confettiInstance = confettiData

        if (this.direction === 'top' || this.direction === 'bottom') {
          let y = this.direction === 'top' ? this.$refs.mediaElement.offsetHeight : document.body.offsetHeight - this.$refs.mediaElement.offsetHeight
          confettiData.makeBang({ x: 0, y: y }, { x: document.body.offsetWidth, y: y }, 10)
        } else {
          let x = this.direction === 'left' ? this.$refs.mediaElement.offsetWidth : document.body.offsetWidth - this.$refs.mediaElement.offsetWidth
          confettiData.makeBang({ x: x, y: 0 }, { x: x, y: document.body.offsetHeight }, 10)
        }

        pushT(setTimeout(() => {
          if (this.confettiInstance && typeof this.confettiInstance.destroy === 'function') {
            this.confettiInstance.destroy()
            this.confettiInstance = null
          }
        }, CONFETTI_DESTROY_AFTER_MS))
      }, 2000))

      pushT(setTimeout(() => {
        // Was a bare axios.get: no keepalive, no timeout, no catch. The other
        // two boxes had each grown a fallback of their own; this one, which
        // draws the majority of the platform's ads, reported into an unhandled
        // rejection whenever the request did not go through.
        reportAdEvent(this.endUrl, 'endUrl')

        this.imageShown = false

        pushT(setTimeout(() => {
          this.$emit('finished', this.$vnode.key)
        }, 1000))
      }, this.duration * 1000 + 1000))
    } catch (e) {
      console.error('finishing because of error', e)
      this.$emit('finished', this.$vnode.key)
    }
  },

  beforeDestroy () {
    if (this.bannerTimeouts) {
      this.bannerTimeouts.forEach((id) => clearTimeout(id))
      this.bannerTimeouts = null
    }
    if (this.confettiInstance && typeof this.confettiInstance.destroy === 'function') {
      try { this.confettiInstance.destroy() } catch (e) { void e }
      this.confettiInstance = null
    }
  },
  methods: {
    /**
     * The creative did not load. Retry once on the other zone; if that is not
     * possible or has already been done, report it rather than running the
     * countdown over an empty frame - which is what this component did until
     * 23.09.2026, for every one-third and extension-universal banner.
     */
    onMediaError (event) {
      if (this.tryMediaFallback()) return

      const el = event && event.target ? event.target : null

      reportAdFailure('media_error', {
        failUrl: this.failUrl,
        taskId: this.taskId,
        contentUrl: this.resolvedContentUrl,
        video: el && el.tagName === 'VIDEO' ? el : null,
        startedAt: this._mountedAt || null
      })
    }
  },
  computed: {
    qrSize () {
      let divider = (this.direction === 'top' || this.direction === 'bottom') ? 2 : 3
      if (this.bannerSize && (this.bannerSize - 50) / divider < 115) {
        return Math.floor((this.bannerSize - 50) / divider)
      } {
        return 115
      }
    },
    hasSrcset () {
      return this.contentSrcset && Object.keys(this.contentSrcset).length > 0
    }
  }
}
</script>

<style lang="scss">
  #one-third-banner-media {
    position: fixed;
    z-index: 5;
    animation-duration: 1s;
    animation-fill-mode: forwards;

    &.appear {
      animation-delay: 1s;
      animation-timing-function: cubic-bezier(1, 0, 1, 1);
    }

    &.disappear {
      animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1)
    }
    
    &.at-top {
      width: 100%;
      top: 0px;
      left: 0px;
      transform: translateY(calc(-100% - 7px));
    }

    &.at-bottom {
      width: 100%;
      bottom: 0px;
      left: 0px;
      transform: translateY(calc(100% + 7px));
    }

    &.at-top .media, &.at-bottom .media {
      width: 100%;
    }

    &.at-left {
      top: 0px;
      left: 0px;
      height: calc(100% - 7px);
      transform: translateX(calc(-100%));
    }

    &.at-right {
      top: 0px;
      right: 0px;
      height: calc(100% - 7px);
      transform: translateX(calc(100%));
    }

    &.at-right .media, &.at-left .media {
      height: 100%;
    }

    &.disappear {
      &.at-top {
        animation-name: one-third-disappear-from-top;
      }
      &.at-bottom {
        animation-name: one-third-disappear-from-bottom;
      }
      &.at-left {
        animation-name: one-third-disappear-from-left;
      }
      &.at-right {
        animation-name: one-third-disappear-from-right;
      }
    }

    &.appear {
      &.at-top {
        animation-name: one-third-appear-from-top;
      }
      &.at-bottom {
        animation-name: one-third-appear-from-bottom;
      }
      &.at-left {
        animation-name: one-third-appear-from-left;
      }
      &.at-right {
        animation-name: one-third-appear-from-right;
      }
    }
  }

  @keyframes one-third-disappear-from-top {
    from { transform: translateY(0%) }
    to { transform: translateY(calc(-100% - 7px)) }
  }
  @keyframes one-third-appear-from-top {
    from { transform: translateY(calc(-100% - 7px)) }
    to { transform: translateY(0%) }
  }
  @keyframes one-third-disappear-from-bottom {
    from { transform: translateY(0%) }
    to { transform: translateY(calc(100% + 7px)) }
  }
  @keyframes one-third-appear-from-bottom {
    from { transform: translateY(calc(100% + 7px)) }
    to { transform: translateY(0%) }
  }
  @keyframes one-third-disappear-from-left {
    from { transform: translateX(0%) }
    to { transform: translateX(calc(-100%)) }
  }
  @keyframes one-third-appear-from-left {
    from { transform: translateX(calc(-100%)) }
    to { transform: translateX(0%) }
  }
  @keyframes one-third-disappear-from-right {
    from { transform: translateX(0%) }
    to { transform: translateX(calc(100%)) }
  }
  @keyframes one-third-appear-from-right {
    from { transform: translateX(calc(100%)) }
    to { transform: translateX(0%) }
  }

  #one-third-animation-layer {
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    z-index: 10;
  }

  #one-third-progress-bar {
    position: fixed;
    height: 7px;
    width: 100%;
    z-index: 7;
    left: 0px;
    /* opacity: 0; */
    animation-duration: 300ms;
    animation-fill-mode: forwards;
    background-color: #192140;
    animation-timing-function: ease;

    &:before {
      display: block;
      content: '';
      position: absolute;
      width: 0px;
      left: 0px;
      top: 0px;
      height: 100%;
      animation-duration: var(--one-third-duration);
      animation-delay: 2s;
      animation-fill-mode: forwards;
      background-color: #4870ff;
      animation-timing-function: linear;
      animation-name: width-grow;
    }

    &.at-top {
      top: 0px;
    }

    &.at-bottom {
      bottom: 0px;
    }

    &.at-left {
      bottom: 0px;
    }

    &.at-right {
      bottom: 0px;
    }
  }

  @keyframes width-grow {
    from { width: 0%; }
    to { width: 100%; }
  }

  .banner-qr {
    position: absolute;

    &.rt {
      top: 15px;
      right: 15px;
    }

    &.rb {
      bottom: 15px;
      right: 15px;
    }

    &.lb {
      bottom: 15px;
      left: 15px;
    }

    &.lb-special {
      bottom: 15px;
      left: calc(15px + 14%);
    }

    &.lt {
      top: 15px;
      left: 15px;
    }
  }
</style>
